/**
 * IMPORTANT! Do not use any services/composables here! (other than the ones defined in the context).
 *
 * This redirect handler should be used solely to determine whether user should be redirected
 * to a certain place or not, based on the application state, which is passed into the exported
 * function as first argument.
 *
 * Only imports that help to determine the redirect are allowed here (like consts or moment).
 */

import { BUSINESS_STATUSES, BILLING_METHODS, LOCAL_STORAGE_KEYS } from '@/lib/consts.ts'
import moment from 'moment'

import type { RedirectContext } from '../types'

// native subscription status messages for "google play and itunes billing" only;
// business exists so we can check billing method here
export const nativeSubscriptionRedirect = ({ query, store, router, logger, emitter, flags }: RedirectContext): unknown => {
  const business = store.getters['business/business']
  const businessStatus = store.getters['business/businessStatus']
  const businessId = store.getters['business/businessId']

  if ([
    BILLING_METHODS.GOOGLE_PLAY,
    BILLING_METHODS.ITUNES,
  ].includes(store.getters['subscription/billingMethod'])) {
    let subscriptionMessageType: string | undefined

    // ONCE EVERY 24 HOURS REDIRECTS
    if (businessStatus === BUSINESS_STATUSES.TRIAL) {
      // "your trial will end soon" (in less than 5 days)
      const trialDaysLeft = moment(business.trial_till).diff(moment(), 'days')
      const lastShownAt = flags.getFlag(LOCAL_STORAGE_KEYS.TRIAL_WARNING_SHOWN_AT, null, businessId)
      const lastShownHoursAgo = moment().diff(moment(lastShownAt || undefined), 'hours')
      if (trialDaysLeft <= 5 && (!lastShownAt || lastShownHoursAgo > 24)) {
        flags.setFlag(LOCAL_STORAGE_KEYS.TRIAL_WARNING_SHOWN_AT, moment().toISOString(), businessId)
        subscriptionMessageType = 'warning'
        emitter.emit(emitter.EVENTS.ANALYTICS_DISPATCH, emitter.EVENTS.SUBSCRIPTION_TRIAL_WILL_EXPIRE_SHOWN)
      }
    }

    if (businessStatus === BUSINESS_STATUSES.ACTIVE_OVERDUE) {
      // "your payment has not been renewed"

      const lastShownAt = flags.getFlag(LOCAL_STORAGE_KEYS.OVERDUE_WARNING_SHOWN_AT, null, businessId)
      const lastShownHoursAgo = moment().diff(moment(lastShownAt || undefined), 'hours')

      if (!lastShownAt || lastShownHoursAgo > 24) {
        flags.setFlag(LOCAL_STORAGE_KEYS.OVERDUE_WARNING_SHOWN_AT, moment().toISOString(), businessId)
        subscriptionMessageType = 'overdue'
      }
    }

    //  A REDIRECTS
    if (businessStatus === BUSINESS_STATUSES.TRIAL_BLOCKED) {
      // "your free trial has expired"
      subscriptionMessageType = 'expired'
      emitter.emit(emitter.EVENTS.ANALYTICS_DISPATCH, emitter.EVENTS.SUBSCRIPTION_TRIAL_HAS_EXPIRED_SHOWN)
    }

    if ([BUSINESS_STATUSES.BLOCKED_OVERDUE, BUSINESS_STATUSES.CHURNED].includes(businessStatus)) {
      // "your account has been disabled"
      subscriptionMessageType = 'disabled'
    }

    if (businessStatus === BUSINESS_STATUSES.BUSINESS_INVALID) {
      // "your account has been blocked"
      subscriptionMessageType = 'blocked'
    }

    if (subscriptionMessageType) {
      logger.debug(`Business status: ${businessStatus} >>> settings-subscription-status ${subscriptionMessageType}`)
      return router.push({
        name: 'settings-subscription-status',
        params: {
          businessId: businessId,
          type: subscriptionMessageType,
        },
        query,
      })
    }
  }
}
